/**
 * The comparison report for two tests (task 1178; extracted from
 * `TestComparisonPage` in task 1227, which made Compare a section of the
 * Testing tab). `a` is the baseline, `b` the candidate:
 *
 *   1. Two summary strips — the strip from the Tests list, each with its
 *      environment and version chips and age.
 *   2. The row-level diff, regressions first; a row expands to both outputs
 *      and both human verdicts.
 *   3. The criteria panel (task 1180) — per-criterion deltas, hidden while the
 *      service returns no criteria.
 *
 * The section (`TestCompareSection`) owns choosing the two tests; this
 * component only reads and renders the report for the pair it is given.
 */
import { Fragment, useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useTestRunComparison } from '@/hooks/useEndpointTestRunGroups';
import { formatRelativeTime } from '@/utils/dateUtils';
import { targetLabel } from '@/utils/testRunResults';
import { TestRunSummaryStrip } from './TestRunSummaryStrip';
import { ComparisonRowDetail } from './ComparisonRowDetail';
import { ComparisonCriteriaPanel } from './ComparisonCriteriaPanel';
import { ScoresTable } from './ScoresTable';
import {
  CHANGE_BADGE_CLASS,
  CHANGE_LABEL,
  outcomeClass,
  outcomeLabel,
  sortComparisonRows,
} from './comparisonDisplay';
import type { ComparisonSide, EndpointTestRunResult } from '@/types/endpointTests';

/** "staging · v1.3", or "working state" — the identity line's target rule (task 1232). */
export function sideTargetLabel(side: Pick<ComparisonSide, 'environment' | 'resourceVersion'> | undefined): string {
  return side ? targetLabel(side) : 'working state';
}

/** Each side's criteria table in an expanded row (task 1180). */
const renderRowScores = (result: EndpointTestRunResult) => <ScoresTable endpointTestRunId={result.id} />;

interface TestComparisonProps {
  /** The AI Service both tests belong to (kept for callers that link back). */
  endpointId: string;
  /** The baseline test. */
  a: string;
  /** The candidate test. */
  b: string;
}

export function TestComparison({ a, b }: TestComparisonProps) {
  const { data: comparison, isLoading, isError } = useTestRunComparison(a, b);
  const [expanded, setExpanded] = useState<string | null>(null);

  if (isLoading) return <p className="text-sm text-gray-500 dark:text-gray-400">Loading comparison…</p>;
  if (isError || !comparison) return <p className="text-sm text-red-500">Couldn’t load this comparison.</p>;

  const rows = sortComparisonRows(comparison.rows);
  const counts = rows.reduce<Record<string, number>>((acc, row) => {
    acc[row.change] = (acc[row.change] ?? 0) + 1;
    return acc;
  }, {});
  const aLabel = sideTargetLabel(comparison.a);
  const bLabel = sideTargetLabel(comparison.b);

  return (
    <div className="space-y-6" data-testid="test-comparison">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {[{ side: comparison.a, role: 'baseline' }, { side: comparison.b, role: 'candidate' }].map(({ side, role }) => (
          <div key={role} className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              <span className="font-medium text-gray-700 dark:text-gray-200">{sideTargetLabel(side)}</span>
              {' '}· {role}{side.createdAt ? ` · ${formatRelativeTime(side.createdAt)}` : ''}
            </p>
            <TestRunSummaryStrip summary={side.summary} environment={side.environment} version={side.resourceVersion} />
          </div>
        ))}
      </div>

      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <div className="flex flex-wrap items-baseline gap-x-3 px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          {/* The rows are scenarios (task 1211); "Tests" names the executions now (task 1227). */}
          <h2 className="text-lg font-medium text-gray-900 dark:text-white">Scenarios</h2>
          <span className="text-xs text-gray-500 dark:text-gray-400" data-testid="comparison-counts">
            {counts.regressed ?? 0} regressed · {counts.improved ?? 0} improved · {counts.unchanged ?? 0} unchanged
            {counts['flipped-criterion'] ? ` · ${counts['flipped-criterion']} flipped criterion` : ''}
            {' '}· {(counts['only-a'] ?? 0) + (counts['only-b'] ?? 0)} only in one test
          </span>
        </div>
        {rows.length === 0 ? (
          <p className="p-4 text-sm text-gray-500 dark:text-gray-400">These tests share no scenarios.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 dark:text-gray-400">
                  <th className="px-4 py-2 font-medium">Scenario</th>
                  <th className="px-4 py-2 font-medium">a · {aLabel}</th>
                  <th className="px-4 py-2 font-medium">b · {bLabel}</th>
                  <th className="px-4 py-2 font-medium">Change</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const open = expanded === row.endpointTestId;
                  return (
                    <Fragment key={row.endpointTestId}>
                      <tr data-testid={`comparison-row-${row.endpointTestId}`} className="border-t border-gray-100 dark:border-gray-700">
                        <td className="px-4 py-2">
                          <button
                            type="button"
                            onClick={() => setExpanded(open ? null : row.endpointTestId)}
                            aria-expanded={open}
                            className="inline-flex items-center gap-1 text-left text-gray-900 dark:text-white hover:text-indigo-600"
                          >
                            {open ? <ChevronDownIcon className="h-3.5 w-3.5" /> : <ChevronRightIcon className="h-3.5 w-3.5" />}
                            {row.name}
                          </button>
                        </td>
                        <td className={`px-4 py-2 text-xs ${outcomeClass(row.a?.outcome)}`}>{outcomeLabel(row.a?.outcome)}</td>
                        <td className={`px-4 py-2 text-xs ${outcomeClass(row.b?.outcome)}`}>{outcomeLabel(row.b?.outcome)}</td>
                        <td className="px-4 py-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${CHANGE_BADGE_CLASS[row.change]}`}>
                            {CHANGE_LABEL[row.change]}
                          </span>
                        </td>
                      </tr>
                      {open && (
                        <tr>
                          <td colSpan={4} className="p-0">
                            <ComparisonRowDetail
                              aId={comparison.a.id}
                              bId={comparison.b.id}
                              aLabel={`a · ${aLabel}`}
                              bLabel={`b · ${bLabel}`}
                              a={row.a}
                              b={row.b}
                              renderExtras={renderRowScores}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {comparison.criteria.length > 0 && (
        <ComparisonCriteriaPanel criteria={comparison.criteria} aLabel={aLabel} bLabel={bLabel} />
      )}
    </div>
  );
}

export default TestComparison;
